// Today as a FEED, not three filters over one list.
//
// The timeline shipped with a `Past · Upcoming · All` segment, and a segment
// that filters is a segment that hides: a punter who wanted to know what the
// pad said yesterday had to leave the screen, and one who wanted to scroll from
// the morning through to tomorrow had to pick "All" first. So the three
// positions stop being filters and become JUMPS, and the screen becomes one
// continuous roll of paper read the way a roll of paper is read — oldest at the
// top, newest at the foot, with the Now rule somewhere in the middle of it.
//
// Above the rule, before today's own started tickets, sits the paper trail: one
// ruled heading per day and the proposals that day settled to, newest day
// nearest the rule. Below it, the rest of today and then tomorrow's board if
// the device happens to hold one.
//
// EVERYTHING HERE IS PURE ARITHMETIC over things already on the device. The
// trail is read from storage, the board was paid for this morning, and NOTHING
// in this file or in anything that reads it may fetch: the note at the foot
// says tomorrow's board "lands when the prices drop" precisely because asking
// for it would cost a credit nobody tapped for.

import { clusterTimeline } from './timeline.js';
import { dayShort } from './format.js';
import { shiftDate } from './paper-trail.js';
import { sportsOf } from './sport-glyphs.js';
import { isRacingSport } from './engine/teams.js';

/** The line at the top once the trail has nothing older left to give. */
export const TRAIL_DONE_NOTE = 'That’s the whole record';

/**
 * The line at the foot when tomorrow's prices are not on the device.
 *
 * "Tomorrow's board lands when the prices drop" was written when Today was one
 * kind of paper. A race card is not a board and it does not drop at 7am: it is
 * posted through the afternoon, meeting by meeting. So the sentence says the
 * one thing that is true of both, and says WHO does it, which is the part a
 * reader can act on: nobody here is fetching it.
 */
export const NO_TOMORROW_NOTE = 'Tomorrow’s prices land when the books post them.';

/**
 * A day's heading on the feed: "Today", "Yesterday", or "Sat 12 Sep".
 *
 * The two nearest days get words because that is how anyone says them, and
 * everything before that gets its date — "Wed" alone stops being a date after a
 * week and starts being a guess.
 */
export function dayHeading(date, today = null) {
  if (!date) return '';
  if (today && date === today) return 'Today';
  if (today && date === shiftDate(today, -1)) return 'Yesterday';
  return dayShort(date);
}

/**
 * The whole feed, in reading order, and where the Now rule landed in it.
 *
 * @param {object} opts
 * @param {Array}  opts.timeline   TODAY's entries only, in `timelineOf` order
 * @param {number} opts.foldIndex  where Now sits inside `timeline`
 * @param {Array}  opts.trailDays  `[{ date, proposals }]`, OLDEST FIRST
 * @param {Array}  opts.tomorrow   entries on tomorrow's board, or empty
 * @param {string} opts.today      'YYYY-MM-DD', for the day headings
 * @param {number} opts.now        epoch ms, for the kick-off labels
 * @param {boolean} opts.trailDone the trail has no older day to load
 * @returns {{ sections: Array, nowIndex: number }}
 *   sections: `{ kind: 'day' | 'now' | 'group' | 'note', key, … }`
 *   nowIndex: the index of the one `now` section
 */
export function feedOf({
  timeline = [],
  foldIndex = 0,
  trailDays = [],
  tomorrow = [],
  today = null,
  now = Date.now(),
  trailDone = false,
} = {}) {
  const sections = [];

  // The top of the roll. Said ONCE, at the top, rather than by an infinite
  // scroller that quietly stops spinning: "nothing loaded" and "there is no
  // more" look identical from the reader's end.
  if (trailDone) sections.push({ kind: 'note', key: 'trail-done', text: TRAIL_DONE_NOTE });

  for (const day of trailDays ?? []) {
    if (!day?.date) continue;
    sections.push({
      kind: 'day',
      key: `day-${day.date}`,
      date: day.date,
      label: dayHeading(day.date, today),
      proposals: day.proposals ?? [],
    });
  }

  const list = timeline ?? [];
  const fold = Math.max(0, Math.min(Number.isFinite(foldIndex) ? foldIndex : list.length, list.length));

  const push = (entries, prefix, day) => {
    for (const g of clusterTimeline(entries, now)) {
      sections.push({
        kind: 'group',
        key: `${prefix}-${g.key}`,
        label: g.label,
        cutoff: g.cutoff,
        entries: g.entries,
        past: prefix === 'p',
        day,
      });
    }
  };

  push(list.slice(0, fold), 'p', 'today');

  const nowIndex = sections.length;
  sections.push({ kind: 'now', key: 'now' });

  push(list.slice(fold), 'f', 'today');

  const board = tomorrow ?? [];
  if (board.length) push(board, 't', 'tomorrow');
  // NOT a fetch, and not a button that would become one. The board arrives on
  // its own when the prices drop; all this screen can honestly do is say so.
  else sections.push({ kind: 'note', key: 'no-tomorrow', text: NO_TOMORROW_NOTE });

  return { sections, nowIndex };
}

/**
 * Where Earlier · Now · Later land: the key of the section each one scrolls to,
 * or null when there is nothing in that direction.
 *
 * Earlier is the TOP OF WHAT IS LOADED rather than the top of the record —
 * there is no bottom to the trail to jump to, only more of it — and Later is
 * the first cluster below the rule, which on a spent evening is tomorrow's.
 */
export function jumpTargets(sections = []) {
  const list = sections ?? [];
  const nowIndex = list.findIndex((s) => s?.kind === 'now');
  const first = list.find((s) => s?.kind === 'day' || s?.kind === 'group') ?? null;
  const later = (nowIndex === -1 ? [] : list.slice(nowIndex + 1)).find((s) => s?.kind === 'group') ?? null;
  return {
    earlier: first?.key ?? null,
    now: nowIndex === -1 ? null : list[nowIndex].key,
    later: later?.key ?? null,
  };
}

// --- two kinds of paper on one roll -------------------------------------------
// Racing arrived on the pad and Today stopped being one kind of ticket. A
// punter who does not follow the horses was reading past half the feed to find
// the football, and one who opened the app at 2 pm for the card was doing the
// same in the other direction.
//
// SO IT IS A FILTER, and it is the only one on this screen — Earlier · Now ·
// Later were made jumps precisely because a segment that hides is a segment
// that loses you the day. The difference is the promise each makes: the jumps
// promise the whole day is there, and this one promises the two kinds are the
// reader's to choose between. BOTH ON IS THE DEFAULT AND IS THE WHOLE FEED, so
// nothing is hidden unless somebody hid it, and the control that hid it is
// stuck to the top of the screen saying so.

/** Everything: the shape `filterFeed` is a no-op for. */
export const FEED_FILTER_ALL = Object.freeze({ sports: true, racing: true });

/**
 * Which kinds of leg a ticket carries: `{ sports, racing }`, and a multi-sport
 * ticket answers true to both.
 *
 * A LEG WITH NO SPORT ON IT COUNTS AS SPORT. Every leg the engine prints
 * carries `sport`, and every racing leg carries one of the three codes
 * (`fromRaces` puts it there), so the only legs that can arrive without one are
 * old records rebuilt from a frozen copy — sports-board tickets, all of them.
 * Counting them as nothing would make them vanish the moment either toggle went
 * off, which is a ticket disappearing for want of a field nobody can see.
 */
export function kindsOf(legs) {
  const sports = sportsOf(legs);
  return {
    racing: sports.some(isRacingSport),
    sports: sports.length === 0 || sports.some((s) => !isRacingSport(s)),
  };
}

/** Is this ticket's set of legs shown under this filter? */
export function shows(legs, { sports = true, racing = true } = {}) {
  const kinds = kindsOf(legs);
  return (kinds.sports && sports) || (kinds.racing && racing);
}

/**
 * The entries a filter leaves on the feed, in the order they came.
 *
 * Pure, and applied BEFORE `feedOf` rather than after it: the fold, the
 * clusters, the jumps and the Now rule are all arithmetic over the list, so a
 * filter applied to the finished sections would leave a cluster heading with
 * nothing under it and a Now rule counted against tickets the reader cannot
 * see. Filter the list, then build the feed out of what is left.
 *
 * A MIXED TICKET IS BOTH. A multi with a race and a football match on it shows
 * whenever either toggle is on — it is half the reader's, and hiding it would
 * be the filter throwing away a ticket neither switch was asked about.
 */
export function filterFeed(entries, filter = FEED_FILTER_ALL) {
  const { sports = true, racing = true } = filter ?? {};
  if (sports && racing) return entries ?? [];
  return (entries ?? []).filter((e) => shows(e?.ticket?.legs ?? e?.legs ?? [], { sports, racing }));
}

/**
 * The same cut over the paper trail: each day's proposals filtered, and a day
 * left with nothing dropped entirely.
 *
 * A heading with no proposals under it is a day the reader is told about and
 * then shown nothing of — worse than a missing day, because it reads as a
 * morning the pad printed nothing on.
 */
export function filterDays(days, filter = FEED_FILTER_ALL) {
  const { sports = true, racing = true } = filter ?? {};
  if (sports && racing) return days ?? [];
  return (days ?? [])
    .map((day) => ({ ...day, proposals: filterFeed(day?.proposals ?? [], { sports, racing }) }))
    .filter((day) => day.proposals.length > 0);
}

/**
 * What is hidden, in words — printed only where the filter is the reason the
 * screen is empty.
 *
 * Said plainly and in the present tense, because the reader did it and can undo
 * it in one tap: the control is two inches above this line and stuck there.
 */
export function hiddenLine({ sports = true, racing = true } = {}) {
  if (!sports && !racing) return 'Sports and racing are both hidden.';
  if (!sports) return 'Sports are hidden.';
  if (!racing) return 'Racing is hidden.';
  return '';
}

/** Which of the three regions a section belongs to — the segment's own value. */
export function regionOf(sections, index) {
  const list = sections ?? [];
  const nowIndex = list.findIndex((s) => s?.kind === 'now');
  if (nowIndex === -1) return 'now';
  if (index < nowIndex) return 'earlier';
  const targets = jumpTargets(list);
  if (!targets.later) return 'now';
  const laterIndex = list.findIndex((s) => s?.key === targets.later);
  return index >= laterIndex ? 'later' : 'now';
}
